/**
 * heliosTelemetry.ts — Lightweight behavioral event tracking for HELIOS.
 *
 * Usage: import { track } from '../services/heliosTelemetry';
 *        track('page_view', { page: '/training' });
 *
 * Rules:
 * - Never throws. All errors are swallowed silently.
 * - No circular dependencies. This file imports only supabase.
 * - Batches events and flushes every 3 seconds or 10 events.
 * - Generates a session ID per page load for grouping events.
 */

import { supabase } from '../lib/supabase';

// ── Session ID ──────────────────────────────────────────────────────

const SESSION_ID = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// ── Event Buffer ────────────────────────────────────────────────────

interface QueuedEvent {
  event: string;
  properties: Record<string, unknown>;
  timestamp: string;
}

const buffer: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const FLUSH_INTERVAL_MS = 3000;
const FLUSH_THRESHOLD = 10;

// ── Core track function ─────────────────────────────────────────────

export function track(event: string, properties?: Record<string, unknown>): void {
  try {
    buffer.push({
      event,
      properties: properties ?? {},
      timestamp: new Date().toISOString(),
    });

    if (buffer.length >= FLUSH_THRESHOLD) {
      flush();
    } else if (!flushTimer) {
      flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS);
    }
  } catch {
    // Never throw
  }
}

// ── Flush to Supabase ───────────────────────────────────────────────

async function flush(): Promise<void> {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  if (buffer.length === 0) return;

  const batch = buffer.splice(0, buffer.length);

  try {
    const { data: authData } = await supabase.auth.getUser();
    const userId = authData.user?.id ?? null;

    const rows = batch.map(e => ({
      user_id: userId,
      event: e.event,
      properties: e.properties,
      session_id: SESSION_ID,
      created_at: e.timestamp,
    }));

    await supabase.from('user_events').insert(rows);
  } catch {
    // Silently fail — telemetry should never crash the app
  }
}

// Flush on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    flush();
  });
}

// ── Helper: Rate AI response ────────────────────────────────────────

export function trackAiRating(
  feature: string,
  rating: 1 | -1,
  telemetryId: string | null,
  meta?: { responseLength?: number; latencyMs?: number },
): void {
  track('ai_response_rated', {
    feature,
    rating,
    telemetry_id: telemetryId,
    response_length: meta?.responseLength,
    latency_ms: meta?.latencyMs,
  });

  // Also update the helios_telemetry row quality score
  if (telemetryId) {
    try {
      supabase.from('helios_telemetry')
        .update({ response_quality_score: rating === 1 ? 5 : 1 })
        .eq('id', telemetryId)
        .then(() => {});
    } catch {
      // non-critical
    }
  }
}

// ── Session ID accessor ─────────────────────────────────────────────

export function getSessionId(): string {
  return SESSION_ID;
}
